import { degraded, problemsOf, received, runSchema, unreadable, type FieldParser, type Received } from 'validation';

export interface ResponseContract<T> {
  /** The schema for the full backend shape of this response. */
  readonly schema: FieldParser<T>;
  /** Label for the validation log — conventionally the endpoint's own name. */
  readonly source: string;
}

/**
 * Turn a raw response body into what we can say about it (issues #1035, #1220).
 *
 * The three arms come straight off the parse: a schema that could not produce a
 * value at all is `unreadable`; one that had to substitute or drop something is
 * `degraded`, carrying both the value and what it cost; one that needed neither
 * is `ok`.
 *
 * It declares no policy. Which of the three a caller can live with is the
 * caller's question — an editor takes only `ok`, a monitoring view would rather
 * show a stale figure than nothing, and a list can say how many rows it lost.
 * The endpoint cannot know which of those it is serving, so it does not decide.
 * What a *field* can say for itself — that an identity has no substitute — is
 * said in the schema, by `REJECT`, where it holds for every consumer because
 * it is true of the field rather than of one page.
 *
 * Returns rather than throws, because whether an unreadable answer is an *error*
 * depends on what the caller was going to do with it. A read whose only consumer
 * displays it collapses the result with `acceptDegraded`; a write's receipt must not,
 * because by then the server has already committed.
 */
export const readResponse = <T>(raw: unknown, contract: ResponseContract<T>): Received<T> => {
  const result = runSchema(contract.schema, raw, contract.source);
  if (!result.ok) return unreadable(problemsOf(result.warnings, 'unreadable', contract.source));
  if (result.warnings.size === 0) return received(result.value);
  return degraded(result.value, problemsOf(result.warnings, 'drift', contract.source));
};
